<template>
    <v-card class="d-flex flex-column" height="400px">
        <v-list-item class="align-start" style="height: 50%; min-height: 220px">
            <v-list-item-title
                class="text-typography1 text-headline-small mb-1 text-wrap"
            >
                {{ library.name }}
            </v-list-item-title>
            <v-list-item-subtitle
                v-if="library.libraryVersions"
                class="text-typography2 mt-1 mb-2 opacity-100"
            >
                {{ library.libraryVersions[0].version }}
            </v-list-item-subtitle>
            <v-list-item-subtitle>
                Type: {{ libTypeStatus }}
            </v-list-item-subtitle>
            <v-list-item-subtitle>
                <v-lazy v-model="isStatsActive">
                    <CalcStatsChips
                        v-if="stats.hasStats && showStats"
                        :no-left-margin="true"
                        :manually-set-vals="true"
                        :disable-clickable="true"
                        :manual-loading="stats.loading"
                        :manual-likes="stats.likes.toString()"
                        :manual-total-projects="
                            stats.numberOfProjects.toString()
                        "
                        :manual-total-usage="
                            stats.totalFunctionCalls.toString()
                        "
                        :manual-unique-users="stats.totalUsers.toString()"
                    />
                </v-lazy>
            </v-list-item-subtitle>
            <template #append>
                <v-img
                    :src="
                        require(`../../../assets/calcBlocks/${
                            library.libraryId % 9
                        }.png`)
                    "
                    width="100%"
                    height="100%"
                    max-height="190px"
                    style="object-fit: contain; flex-shrink: 0"
                />
            </template>
        </v-list-item>
        <v-card-text
            class="
                text-typography1
                overflow-y-auto
                styled-scrollbar
                text-body-medium
            "
            v-if="library.libraryVersions"
            style="max-height: 230px"
        >
            <span v-if="longDesc(library.libraryVersions[0].description)">
                {{ library.libraryVersions[0].description }}
            </span>
            <span v-else>
                This is a library of calculations hosted on Arup Compute, the
                library developer did not provide a description for this
                library.
            </span>
        </v-card-text>
        <v-card-actions class="justify-space-between pb-4 pt-2">
            <v-btn
                rounded
                variant="outlined"
                v-if="library.libraryVersions"
                color="secondary"
                class="text-capitalize"
                :to="{ path: `/library/${library.libraryId}?path=latest` }"
            >
                Explore Library
            </v-btn>
            <span class="text-typography1" v-if="!library.libraryVersions">
                This library has no versions yet
            </span>
            <CardDialog v-else :library="library" />
            <NewDialog
                v-if="ownLib"
                :library="library"
                :libraries="libraries"
            />
            <StatusDialog v-if="admin" :libver="library.libraryVersions[0]" />
        </v-card-actions>
    </v-card>
</template>

<script setup>
import CardDialog from './CardDialog.vue'
import NewDialog from './NewDialog.vue'
import StatusDialog from './StatusDialog.vue'
import CalcStatsChips from '../calc/calc-info/CalcStatsChips.vue'
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'

const props = defineProps({
    library: { type: Object, required: true },
    libraries: { required: true, type: Array },
})

const store = useStore()
const user = computed(() => store.state.user)
const admins = computed(() => store.state.admins)
const libCategories = computed(() => store.state.libCategories)
const showStats = computed(() => store.state.showStats)

const stats = ref({
    hasStats: true,
    loading: true,
    numberOfProjects: 0,
    likes: 0,
    totalFunctionCalls: 0,
    totalUsers: 0,
})
const isStatsActive = ref(false)

const ownLib = computed(
    () =>
        props.library.user.username.toLowerCase() ===
            user.value.mail.toLowerCase() || admin.value
)
const admin = computed(() =>
    admins.value.includes(user.value.mail.toLowerCase())
)
const libTypeStatus = computed(() => {
    if (props.library.libraryVersions[0].libraryVersionStatusId) {
        return libCategories.value[
            props.library.libraryVersions[0].libraryVersionStatusId
        ]
    }
})

function compact(number) {
    return new Intl.NumberFormat('en', {
        notation: 'compact',
        compactDisplay: 'short',
    }).format(number)
}
function longDesc(description) {
    return description.split(' ').length > 1
}

watch(isStatsActive, async (newVal, oldVal) => {
    if (newVal && !oldVal) {
        store
            .dispatch('getLibrariesStats', [props.library.libraryId])
            .then((res) => {
                if (res.length > 0 && res[0].numberOfProjects != undefined) {
                    const val = res[0]
                    stats.value.likes = compact(val.likes)
                    stats.value.numberOfProjects = compact(val.numberOfProjects)
                    stats.value.totalFunctionCalls = compact(
                        val.totalFunctionCalls
                    )
                    stats.value.totalUsers = compact(val.totalUsers)
                } else {
                    stats.value.hasStats = false
                }
                stats.value.loading = false
            })
    }
})
</script>

<style scoped>
.v-card--reveal {
    bottom: 0;
    opacity: 1 !important;
    position: absolute;
    width: 100%;
}
.v-card__text {
    text-align: start;
}
:deep(.v-list-item__content) {
    align-self: flex-start;
    padding: 12px 0px;
}
:deep(.v-list-item__append) {
    width: 180px;
    padding: 8px;
    margin: 8px;
}
</style>
